/* mo-prot-spill.js: "The spill test" on the mattress protectors page (sections/mo-prot.liquid), 2026-10-14.
   A cut through the top of a mattress with the protector on it. Water falls where the shopper taps, lands on the
   face fabric, beads, soaks sideways through the fabric, and stops at the waterproof barrier: the mattress below
   stays dry. Shown only for tiers Ashley calls waterproof ("Quiet waterproof barrier protects your mattress without
   affecting its feel"); the Premium Tencel tier has no published waterproof claim, so it gets no water.
   Ideas ported to plain canvas, no dependency: 21st.dev Metaball Morph 27828 (drops merge like mercury),
   Water Ripple Image Carousel 36223 and Ripple Buttons 26809 (the ring where a drop lands).
   It shows the idea, not a lab test: layer thicknesses are not published. Canvas colors are plain hex. */
(function () {
  'use strict';
  var REDUCED = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Face profiles traced from the store's product photos: Basic quilts in a diagonal puff, Advanced is the
  // "Luxury plush cooling" quilt, Extra Cooling a fine wave knit; Premium Tencel is drawn flat (no photo of it).
  var TIERS = {
    basic: { period: 30, amp: 4.5, face: '#f1f0ec', shade: '#d6d4cd', wet: '#9fb4bd', waterproof: true, cool: true },
    advanced: { period: 42, amp: 6.5, face: '#eef1f2', shade: '#cfd6d9', wet: '#9cb3bf', waterproof: true, cool: true },
    cooling: { period: 11, amp: 2.2, face: '#edf2f4', shade: '#c9d4d9', wet: '#98b2be', waterproof: true, cool: true },
    tencel: { period: 0, amp: 0, face: '#f3f1ea', shade: '#d9d5ca', wet: '#a8b6ba', waterproof: false, cool: false }
  };
  var C = {
    ink: '#16181a', mat: '#d8d3c9', matLine: '#b9b2a5', matDeep: '#c4bdb0', barrier: '#7d929c', barrierHot: '#c9e2ee',
    water: '#8cc3dd', waterDeep: '#4f8fae', hi: '#ffffff', wisp: '#a9bcc6', label: '#e8e4dc', dim: '#8d8a84', ring: '#cfe5ef'
  };

  function Spill(root) {
    this.root = root;
    this.canvas = root.querySelector('canvas');
    this.ctx = this.canvas.getContext('2d');
    this.labels = {
      face: root.getAttribute('data-label-face') || 'Face fabric',
      barrier: root.getAttribute('data-label-barrier') || 'Waterproof barrier',
      barrierNone: root.getAttribute('data-label-barrier-none') || 'Barrier: not published',
      mattress: root.getAttribute('data-label-mattress') || 'Your mattress',
      wick: root.getAttribute('data-label-wick') || 'moisture-wicking'
    };
    this.font = getComputedStyle(root).fontFamily || 'Inter, sans-serif';
    this.tierKey = root.getAttribute('data-tier') || 'basic';
    this.drops = []; this.beads = []; this.air = []; this.rings = []; this.wisps = [];
    this.wet = null; this.hot = null; this.count = 0; this.running = false; this.last = 0; this.idle = 0;
    this.lastDrop = -1e9; this.t = 0;
    var self = this;
    this.resize();
    this.reset(true);
    if ('ResizeObserver' in window) new ResizeObserver(function () { self.resize(); self.draw(); }).observe(this.canvas);
    else addEventListener('resize', function () { self.resize(); self.draw(); });
    this.canvas.addEventListener('pointerdown', function (e) {
      var r = self.canvas.getBoundingClientRect();
      self.drop((e.clientX - r.left) / r.width * self.W);
    });
    var btn = root.querySelector('[data-spill-drop]');
    if (btn) btn.addEventListener('click', function () {
      var bx = self.beads.length ? self.beads[0].x : self.W * .6;
      self.drop(Math.max(self.W * .3, Math.min(self.W * .9, bx + (Math.random() < .5 ? -1 : 1) * self.W * (.08 + Math.random() * .12))));
    });
    var reset = root.querySelector('[data-spill-reset]');
    if (reset) reset.addEventListener('click', function () { self.reset(false); });
    root.querySelectorAll('[data-spill-tier]').forEach(function (b) {
      b.addEventListener('click', function () { self.setTier(b.getAttribute('data-spill-tier')); });
    });
    this.status = root.querySelector('[data-spill-status]');
    this.live = root.querySelector('[data-spill-live]');
    root.classList.add('is-live');
    // one orchestrated moment on first view: a drop joins the bead already sitting there, then it waits
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (en) {
          self.visible = en.isIntersecting;
          if (en.isIntersecting && !self.greeted && !self.root.hasAttribute('data-still')) {
            self.greeted = true;
            if (TIERS[self.tierKey].waterproof && !REDUCED) setTimeout(function () { self.drop(self.W * .67, true); }, 650);
          }
          if (en.isIntersecting) self.kick();
        });
      }, { threshold: .35 });
      io.observe(this.canvas);
    }
  }

  Spill.prototype.resize = function () {
    var r = this.canvas.getBoundingClientRect();
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.W = Math.max(280, r.width); this.H = Math.max(180, r.height);
    this.canvas.width = Math.round(this.W * dpr); this.canvas.height = Math.round(this.H * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.u = Math.min(this.W / 760, this.H / 430) * 1.0;
    this.u = Math.max(.55, this.u);
    this.sy = this.H * .47;            // top of the face fabric
    this.tf = Math.max(26, this.H * .16); // face fabric thickness (not published; shows order, not a measurement)
    this.by = this.sy + this.tf;        // waterproof barrier
    this.cols = Math.ceil(this.W / 2) + 2;
    if (!this.wet || this.wet.length !== this.cols) { this.wet = new Float32Array(this.cols); this.hot = new Float32Array(this.cols); }
  };

  Spill.prototype.surface = function (x) {
    var t = TIERS[this.tierKey];
    if (!t.period) return this.sy;
    var p = t.period * 1.5 * this.u, a = t.amp * 1.5 * this.u;
    return this.sy - a * Math.pow(Math.abs(Math.sin(Math.PI * x / p)), .55);
  };

  Spill.prototype.massR = function (m) { return 46 * this.u * Math.sqrt(m); };

  // the finished state the section opens on: one bead resting on the fabric, a damp patch under it, dry below
  Spill.prototype.reset = function (first) {
    this.beads = []; this.air = []; this.drops = []; this.rings = []; this.wisps = [];
    this.wet.fill(0); this.hot.fill(0); this.count = 0;
    if (TIERS[this.tierKey].waterproof) {
      var x = this.W * .56;
      this.beads.push({ x: x, vx: 0, m: 1.05 });
      var c = Math.round(x / 2), span = Math.round(150 * this.u / 2);
      for (var i = -span; i <= span; i++) {
        var k = c + i; if (k < 0 || k >= this.cols) continue;
        var f = i / (span * .5);
        this.wet[k] = 1.3 * Math.exp(-f * f);
        this.hot[k] = 0;
      }
      this.count = 1;
    }
    this.say(first);
    this.draw();
  };

  Spill.prototype.setTier = function (key) {
    if (!TIERS[key] || key === this.tierKey) return;
    this.tierKey = key;
    this.root.setAttribute('data-tier', key);
    var self = this;
    this.root.querySelectorAll('[data-spill-tier]').forEach(function (b) {
      var on = b.getAttribute('data-spill-tier') === key;
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on && b.getAttribute('data-face')) self.labels.face = b.getAttribute('data-face');
    });
    var btn = this.root.querySelector('[data-spill-drop]');
    if (btn) btn.disabled = !TIERS[key].waterproof;
    this.reset(false);
  };

  Spill.prototype.say = function (quiet) {
    var t = TIERS[this.tierKey];
    var msg;
    if (!t.waterproof) msg = this.root.getAttribute('data-msg-none') || 'No published waterproof claim, so no spill test.';
    else msg = (this.root.getAttribute('data-msg-dry') || 'Drops on top: {n}. Mattress below: dry.').replace('{n}', this.count);
    if (this.status) this.status.textContent = msg;
    if (this.live && !quiet) this.live.textContent = msg;
  };

  Spill.prototype.drop = function (x, auto) {
    if (!TIERS[this.tierKey].waterproof) return;
    x = Math.max(this.W * .22, Math.min(this.W * .96, x));
    this.lastDrop = this.t;
    if (REDUCED) { // end frame: the drop is already part of the bead
      this.land(x, 1, true);
      this.settleAll();
      this.draw();
      return;
    }
    this.drops.push({ x: x, y: -30 * this.u, vy: 60, m: .9 + Math.random() * .3 });
    this.kick();
  };

  Spill.prototype.land = function (x, m, quietSplash) {
    this.count++;
    var s = this.surface(x);
    this.rings.push({ x: x, y: s, age: 0 });
    var body = quietSplash ? m : m * .74;
    this.beads.push({ x: x, vx: 0, m: body });
    if (!quietSplash) {
      var n = 7, sm = (m - body) / n;
      for (var i = 0; i < n; i++) {
        var dir = i % 2 ? 1 : -1, sp = (50 + Math.random() * 120) * this.u;
        this.air.push({ x: x + dir * 10 * this.u, y: s - 6 * this.u, vx: dir * sp, vy: -(150 + Math.random() * 220) * this.u, m: sm });
      }
    }
    this.say(false);
  };

  Spill.prototype.settleAll = function () {
    for (var k = 0; k < 60; k++) this.stepBeads(1 / 30, true);
  };

  Spill.prototype.kick = function () {
    if (this.running) return;
    this.running = true; this.last = performance.now(); this.idle = 0;
    var self = this;
    requestAnimationFrame(function f(now) {
      var dt = Math.min(.033, (now - self.last) / 1000); self.last = now;
      var busy = self.step(dt);
      self.draw();
      self.idle = busy ? 0 : self.idle + dt;
      if (self.idle > .4 || (self.visible === false && !busy)) { self.running = false; return; }
      requestAnimationFrame(f);
    });
  };

  Spill.prototype.step = function (dt) {
    this.t += dt;
    var busy = false, g = 2100 * this.u, i;
    for (i = this.drops.length - 1; i >= 0; i--) {
      var d = this.drops[i]; busy = true;
      d.vy += g * dt; d.y += d.vy * dt;
      var r = this.massR(d.m) * .42;
      if (d.y + r >= this.surface(d.x)) { this.drops.splice(i, 1); this.land(d.x, d.m, false); }
    }
    for (i = this.air.length - 1; i >= 0; i--) {
      var a = this.air[i]; busy = true;
      a.vy += g * dt; a.x += a.vx * dt; a.y += a.vy * dt;
      if (a.vy > 0 && a.y >= this.surface(a.x)) { this.air.splice(i, 1); this.beads.push({ x: a.x, vx: a.vx * .25, m: a.m }); }
    }
    if (this.stepBeads(dt, false)) busy = true;
    if (this.stepWet(dt)) busy = true;
    for (i = this.rings.length - 1; i >= 0; i--) { this.rings[i].age += dt; busy = true; if (this.rings[i].age > .7) this.rings.splice(i, 1); }
    if (this.stepWisps(dt)) busy = true;
    return busy;
  };

  // beads slide, pull toward each other within reach and merge (the metaball idea, in one dimension along the fabric)
  Spill.prototype.stepBeads = function (dt, fast) {
    var b = this.beads, moving = false, i, j;
    for (i = 0; i < b.length; i++) {
      var bi = b[i], f = 0, Ri = this.massR(bi.m);
      for (j = 0; j < b.length; j++) {
        if (i === j) continue;
        var dx = b[j].x - bi.x, reach = (Ri + this.massR(b[j].m)) * 1.6;
        if (Math.abs(dx) < reach) f += (dx > 0 ? 1 : -1) * b[j].m * 900 * this.u * (1 - Math.abs(dx) / reach);
      }
      bi.vx += f * dt; bi.vx *= Math.exp(-7 * dt);
      bi.x += bi.vx * dt;
      if (Math.abs(bi.vx) > 2) moving = true;
    }
    for (i = 0; i < b.length; i++) for (j = i + 1; j < b.length; j++) {
      if (Math.abs(b[i].x - b[j].x) < (this.massR(b[i].m) + this.massR(b[j].m)) * .45) {
        var m = b[i].m + b[j].m;
        b[i].x = (b[i].x * b[i].m + b[j].x * b[j].m) / m; b[i].vx = (b[i].vx * b[i].m + b[j].vx * b[j].m) / m;
        b[i].m = m; b.splice(j, 1); j--; moving = true;
      }
    }
    // the fabric drinks a little of each bead; the water then wicks sideways inside the face fabric
    for (i = 0; i < b.length; i++) {
      var take = b[i].m < .22 ? Math.min(b[i].m, .25 * dt) : Math.min(b[i].m * .35 * dt, Math.max(0, b[i].m - .72) * 1.5 * dt);
      if (fast) take = 0;
      if (take > 1e-4) {
        b[i].m -= take; moving = true;
        if (b[i].m < .01) { b.splice(i, 1); i--; continue; }
        var c = Math.round(b[i].x / 2), span = Math.max(2, Math.round(this.massR(b[i].m) / 2));
        for (var k = -span; k <= span; k++) { var q = c + k; if (q >= 0 && q < this.cols) this.wet[q] += take * 9 / (2 * span + 1); }
      }
    }
    return moving;
  };

  Spill.prototype.stepWet = function (dt) {
    var w = this.wet, n = this.cols, active = false, D = Math.min(.45, 14 * dt), i;
    var next = new Float32Array(n);
    for (i = 0; i < n; i++) {
      var l = w[i - 1] !== undefined ? w[i - 1] : w[i], r = w[i + 1] !== undefined ? w[i + 1] : w[i];
      next[i] = w[i] + D * .25 * (l - 2 * w[i] + r) * (w[i] > .05 || l > .05 || r > .05 ? 1 : 0);
      if (Math.abs(next[i] - w[i]) > 1e-3) active = true;
      // a full column reaches the barrier; the barrier answers with a short glow and stops it
      if (next[i] >= 1 && this.hot[i] < 1 && this.t - this.lastDrop < 6) { this.hot[i] = 1; }
      if (this.hot[i] > 0) { this.hot[i] = Math.max(0, this.hot[i] - dt * .9); active = true; }
    }
    this.wet = next;
    return active && this.t - this.lastDrop < 8;
  };

  // moisture leaving the damp fabric, drawn only for tiers Ashley calls moisture-wicking and cool-to-the-touch
  Spill.prototype.stepWisps = function (dt) {
    var t = TIERS[this.tierKey], live = false;
    if (t.cool && !REDUCED && this.t - this.lastDrop < 5.5) {
      for (var k = 0; k < 2; k++) {
        var i = Math.floor(Math.random() * this.cols);
        if (this.wet[i] > .5 && this.wisps.length < 9 && Math.random() < .35) this.wisps.push({ x: i * 2, y: this.surface(i * 2) - 4 * this.u, age: 0, ph: Math.random() * 6.28 });
      }
    }
    for (var j = this.wisps.length - 1; j >= 0; j--) {
      var p = this.wisps[j]; p.age += dt; p.y -= 26 * this.u * dt; live = true;
      if (p.age > 3.2) this.wisps.splice(j, 1);
    }
    return live;
  };

  Spill.prototype.draw = function () {
    var ctx = this.ctx, W = this.W, H = this.H, u = this.u, t = TIERS[this.tierKey], x, i;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = C.ink; ctx.fillRect(0, 0, W, H);

    // the mattress under the protector: quilted ticking, cut open
    ctx.fillStyle = C.mat; ctx.fillRect(0, this.by + 3 * u, W, H - this.by);
    ctx.strokeStyle = C.matLine; ctx.lineWidth = 1;
    var q = 46 * u, qy = this.by + 3 * u;
    ctx.beginPath();
    for (x = -q; x < W + q; x += q) {
      ctx.moveTo(x, qy + 4 * u);
      ctx.quadraticCurveTo(x + q / 2, qy + 26 * u, x + q, qy + 4 * u);
    }
    ctx.stroke();
    ctx.fillStyle = C.matDeep; ctx.fillRect(0, qy + 34 * u, W, H - qy);
    ctx.globalAlpha = .35; ctx.strokeStyle = C.matLine;
    for (var yy = qy + 46 * u; yy < H; yy += 9 * u) { ctx.beginPath(); ctx.moveTo(0, yy); ctx.lineTo(W, yy); ctx.stroke(); }
    ctx.globalAlpha = 1;

    // waterproof barrier (drawn dashed and labelled "not published" on a tier without the claim)
    if (t.waterproof) {
      ctx.fillStyle = C.barrier; ctx.fillRect(0, this.by, W, 3 * u);
      for (i = 0; i < this.cols; i++) if (this.hot[i] > 0) {
        ctx.globalAlpha = this.hot[i] * .9; ctx.fillStyle = C.barrierHot; ctx.fillRect(i * 2 - 1, this.by - 1, 3, 3 * u + 2);
      }
      ctx.globalAlpha = 1;
    } else {
      ctx.strokeStyle = C.dim; ctx.setLineDash([6 * u, 6 * u]); ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(0, this.by + 1.5 * u); ctx.lineTo(W, this.by + 1.5 * u); ctx.stroke(); ctx.setLineDash([]);
    }

    // face fabric with its quilt profile
    ctx.beginPath(); ctx.moveTo(0, this.by);
    for (x = 0; x <= W; x += 2) ctx.lineTo(x, this.surface(x));
    ctx.lineTo(W, this.by); ctx.closePath();
    var gr = ctx.createLinearGradient(0, this.sy - 8 * u, 0, this.by);
    gr.addColorStop(0, t.face); gr.addColorStop(1, t.shade);
    ctx.fillStyle = gr; ctx.fill();
    if (t.period) { // stitch lines where the quilt pulls down
      ctx.strokeStyle = t.shade; ctx.lineWidth = 1;
      var p = t.period * 1.5 * u; ctx.beginPath();
      for (x = 0; x <= W; x += p) { ctx.moveTo(x, this.sy); ctx.lineTo(x, this.sy + this.tf * .55); }
      ctx.stroke();
    }

    // water held in the face fabric: darker where it has soaked, full columns reach the barrier and stop there
    ctx.fillStyle = t.wet;
    for (i = 0; i < this.cols; i++) {
      var w = Math.min(1, this.wet[i]);
      if (w < .02) continue;
      var top = this.surface(i * 2), ww = w * w * (3 - 2 * w);
      ctx.globalAlpha = .12 + .6 * ww;
      ctx.fillRect(i * 2, top + 1, 2.2, (this.by - top - 1) * Math.min(1, .25 + w * .85));
    }
    ctx.globalAlpha = 1;

    // rings where drops landed
    for (i = 0; i < this.rings.length; i++) {
      var rg = this.rings[i], k = rg.age / .7;
      ctx.globalAlpha = (1 - k) * .8; ctx.strokeStyle = C.ring; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.ellipse(rg.x, rg.y, 8 * u + 70 * u * k, (8 * u + 70 * u * k) * .16, 0, 0, 6.283); ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // beads: lens profiles unioned along the fabric so close beads read as one body of water
    this.drawBeads();
    for (i = 0; i < this.air.length; i++) this.blob(this.air[i].x, this.air[i].y, this.massR(this.air[i].m) * .42, 0);
    for (i = 0; i < this.drops.length; i++) {
      var d = this.drops[i], r = this.massR(d.m) * .42, st = Math.min(.45, d.vy / (2600 * u));
      this.blob(d.x, d.y, r, st);
    }

    // moisture leaving the surface: cool neutral, never red
    if (this.wisps.length) {
      ctx.strokeStyle = C.wisp; ctx.lineWidth = 1.1 * u; ctx.lineCap = 'round';
      for (i = 0; i < this.wisps.length; i++) {
        var wp = this.wisps[i], a = Math.sin(Math.PI * Math.min(1, wp.age / 3.2));
        ctx.globalAlpha = a * .4; ctx.beginPath();
        for (var s = 0; s < 9; s++) {
          var yy2 = wp.y - s * 6 * u, xx = wp.x + Math.sin(wp.ph + wp.age * 1.4 + s * .45) * (2 + s * .9) * u;
          if (s) ctx.lineTo(xx, yy2); else ctx.moveTo(xx, yy2);
        }
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    this.drawLabels();
  };

  Spill.prototype.drawBeads = function () {
    var b = this.beads; if (!b.length) return;
    var ctx = this.ctx, u = this.u, n = Math.ceil(this.W / 2) + 1, h = new Float32Array(n), i, x;
    for (i = 0; i < b.length; i++) {
      var R = this.massR(b[i].m), Hh = R * (.5 + .22 * (1 - Math.min(1, b[i].m * 2.5)));
      var a = Math.max(0, Math.floor((b[i].x - R) / 2)), z = Math.min(n - 1, Math.ceil((b[i].x + R) / 2));
      for (var c = a; c <= z; c++) {
        var q = (c * 2 - b[i].x) / R, v = q * q < 1 ? Hh * Math.pow(1 - q * q, .62) : 0;
        h[c] = Math.sqrt(h[c] * h[c] + v * v); // smooth union
      }
    }
    var start = -1;
    for (i = 0; i <= n; i++) {
      var on = i < n && h[i] > .6;
      if (on && start < 0) start = i;
      if (!on && start >= 0) {
        var x0 = start * 2, x1 = (i - 1) * 2, top = 1e9;
        ctx.beginPath(); ctx.moveTo(x0, this.surface(x0));
        for (var c2 = start; c2 < i; c2++) { var yy = this.surface(c2 * 2) - h[c2]; ctx.lineTo(c2 * 2, yy); if (yy < top) top = yy; }
        ctx.lineTo(x1, this.surface(x1));
        for (x = x1; x >= x0; x -= 2) ctx.lineTo(x, this.surface(x) + 1.5 * u);
        ctx.closePath();
        var gr = ctx.createLinearGradient(0, top, 0, this.sy + 2 * u);
        gr.addColorStop(0, C.water); gr.addColorStop(1, C.waterDeep);
        ctx.globalAlpha = .82; ctx.fillStyle = gr; ctx.fill(); ctx.globalAlpha = 1;
        // highlight along the upper left of the bead
        var mid = Math.round((start + i) / 2), w = (i - start);
        ctx.strokeStyle = C.hi; ctx.lineWidth = 2 * u; ctx.lineCap = 'round'; ctx.globalAlpha = .75;
        ctx.beginPath();
        var hs = Math.max(start + 1, Math.round(start + w * .2)), he = Math.round(start + w * .45);
        for (var c3 = hs; c3 <= he; c3++) { var y3 = this.surface(c3 * 2) - h[c3] + 3.2 * u; if (c3 === hs) ctx.moveTo(c3 * 2, y3); else ctx.lineTo(c3 * 2, y3); }
        ctx.stroke(); ctx.globalAlpha = 1;
        ctx.fillStyle = C.hi; ctx.globalAlpha = .9;
        ctx.beginPath(); ctx.arc(start * 2 + w * 2 * .62, this.surface(mid * 2) - h[mid] * .72, 1.8 * u, 0, 6.283); ctx.fill(); ctx.globalAlpha = 1;
        start = -1;
      }
    }
  };

  Spill.prototype.blob = function (x, y, r, stretch) {
    var ctx = this.ctx;
    ctx.beginPath();
    ctx.ellipse(x, y, r * (1 - stretch * .35), r * (1 + stretch), 0, 0, 6.283);
    ctx.globalAlpha = .9; ctx.fillStyle = C.water; ctx.fill();
    ctx.globalAlpha = .85; ctx.fillStyle = C.hi;
    ctx.beginPath(); ctx.arc(x - r * .32, y - r * .35, Math.max(1, r * .22), 0, 6.283); ctx.fill();
    ctx.globalAlpha = 1;
  };

  Spill.prototype.drawLabels = function () {
    var ctx = this.ctx, u = this.u, t = TIERS[this.tierKey], x0 = 18 * u + 6;
    var fs = Math.max(11, Math.round(13 * u));
    ctx.font = '600 ' + fs + 'px ' + this.font;
    ctx.textBaseline = 'middle';
    function tag(txt, y, col) {
      ctx.fillStyle = col; ctx.globalAlpha = .95; ctx.fillText(txt, x0 + 14 * u, y); ctx.globalAlpha = 1;
      ctx.fillRect(x0, y - .5, 9 * u, 1.2);
    }
    tag(this.labels.face, this.sy + this.tf * .5, C.ink);
    if (t.waterproof) tag(this.labels.barrier, this.by + 14 * u, '#2b3a40');
    else tag(this.labels.barrierNone, this.by + 14 * u, '#4a4740');
    tag(this.labels.mattress, this.by + 52 * u, '#4a4740');
  };

  function mount() {
    document.querySelectorAll('[data-prot-spill]').forEach(function (el) {
      if (el.__spill) return;
      el.__spill = new Spill(el);
    });
  }
  window.MOProtSpill = Spill;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount); else mount();
})();
